import { template as _$template } from "solid-js/web";
import { insert as _$insert } from "solid-js/web";
import { memo as _$memo } from "solid-js/web";
import { createComponent as _$createComponent } from "solid-js/web";
var _tmpl$ = /*#__PURE__*/_$template(`<div class=solid-wf-denied>`),
  _tmpl$2 = /*#__PURE__*/_$template(`<div class=solid-wf-loading><span class=solid-spinner>`);
/**
  * @class mmodSolidWorkflow
  * @extends mmodSolidModule
  * @description
  * Base class for Solid GUI modules backed by a server workflow (msvrWorkflow). It owns one
  * WorkflowClient binding and the protocol/GUI patterns every workflow screen repeats:
  *
  *   · lifecycle: full start (model + watch) on `mmUserLogged` (pre-login requests are
  *     ACL-denied, and login re-fires after reconnect so the watch re-registers), pushed
  *     `mmObjects` routed into the reconciler, watch stopped on `mmExit`
  *   · `openRecord(id)` — select + full fetch with history logs; `recordOpened(record)`
  *     hook for module-specific enrichment
  *   · `runAction(record, action)` — model-driven dispatch: `customAction()` override first,
  *     then the action's form through `openForm()`, destructive actions behind a confirm
  *     modal, errors in an alert modal, history refreshed after the action
  *   · `tr()` workflow-prefixed translations, `actionColor()` from the model,
  *     `generateNewId()` per the model's id_generator
  *
  * Views use the `useWorkflow()` hook: { t, tr, tx, wf, client, actionColor, module, ... }.
  */

import { Show } from 'solid-js';
import mmodSolidModule, { useMosy } from 'mmodSolidModule';
import WorkflowClient from './WorkflowClient';

/** Workflow action `color` → button class (same palette the legacy framework used). */
export const ACTION_CLASS = {
  green: 'primary',
  red: 'danger',
  blue: 'info',
  orange: 'warning'
};

/** Workflow hook for views: module context + workflow-aware translations and helpers. */
export function useWorkflow() {
  const ctx = useMosy();
  const m = ctx.module;
  return {
    ...ctx,
    t: key => m.t(key),
    tr: key => m.tr(key),
    tx: text => m.tx(text),
    wf: () => m.wf(),
    client: m.wfclient,
    actionColor: (record, action) => m.actionColor(record, action)
  };
}

/**
 * Access gate for a workflow screen. Renders `children` only once the actor's model has
 * loaded; while it is still loading it shows a spinner, and when the workflow denied the
 * model (empty reply — the actor fails the workflow's `allow`) it shows a centered
 * access-denied panel INSTEAD of the screen chrome. Wrap the module's screen with it:
 * `<WorkflowGate><OfficeScreen/></WorkflowGate>`. Modals/wizards stay outside the gate.
 */
export function WorkflowGate(props) {
  const {
    wf,
    t
  } = useWorkflow();
  return _$createComponent(Show, {
    get when() {
      return wf().model;
    },
    get fallback() {
      return _$createComponent(Show, {
        get when() {
          return !wf().loading;
        },
        get fallback() {
          return _tmpl$2();
        },
        get children() {
          var _el$ = _tmpl$();
          _$insert(_el$, () => wf().error || t('wf.generic.NoAccess'));
          return _el$;
        }
      });
    },
    get children() {
      return props.children;
    }
  });
}
export default class mmodSolidWorkflow extends mmodSolidModule {
  constructor(id, cfg, cfg_schema, dependencies) {
    dependencies = _.union([], dependencies);
    cfg_schema = _.extend({
      workflow_server: {
        type: 'string',
        default: '',
        description: 'Listener acting as the server-side for the workflow (e.g. ":msvrTzRecords")'
      },
      query: {
        type: 'object',
        default: {},
        description: 'Server-side filter for the watched list (mmWatch/mmGet query)'
      },
      limit: {
        type: 'integer',
        default: 500,
        description: 'Records per server page (the first page is watched live; more load on scroll)'
      },
      sort: {
        type: 'array',
        items: {
          type: 'string'
        },
        default: [],
        description: "Server-side sort criteria (e.g. ['event_dt-'])"
      },
      suspended: {
        type: 'string',
        enum: ['', 'hidden', 'disabled'],
        default: MOSY_APP === 'mOffice' ? 'hidden' : 'disabled',
        description: 'how a denied/suspended module reports itself in mmStatus (hidden removes it from the app bar, like legacy mmodWorkflow)'
      }
    }, cfg_schema);
    super(id, cfg, cfg_schema, dependencies);
    this.wfclient = new WorkflowClient(this, {
      server: this.config.workflow_server,
      query: this.config.query,
      limit: this.config.limit,
      sort: Array.isArray(this.config.sort) && this.config.sort.length > 0 ? this.config.sort : undefined,
      // a declared list_fields keeps heavy blob fields out of the watched rows — the targeted
      // fetchFull still pulls complete records (it never restricts fields)
      fields: Array.isArray(this.config.list_fields) && this.config.list_fields.length > 0 ? this.config.list_fields : undefined
    });
  }
  initialStore() {
    return {
      ...super.initialStore(),
      selected: '' // opened record id (read drawer)
    };
  }

  /** The reactive slice the client maintains: model, live list, version, loading, error. */
  wf() {
    return this.store.wf;
  }

  // ---- lifecycle / protocol wiring ----

  /**
  * @description
  * information on changed user authorization; user_id is empty if not logged.
  * Runs the FULL start (model + watch) — the watch registration also recovers
  * after a reconnect, because login re-fires then.
  */
  async mmUserLogged(sender, params) {
    if (!(params.user_id > 0)) return;
    await this.wfclient.start();
    this.toggleModuleVisibility();
  }

  /**
  * @description
  * Reports the module's availability to the app bars via mmStatus, exactly like legacy
  * mmodWorkflow.toggleModuleVisibility: 'ok' once the actor's model loaded (access granted),
  * otherwise the configured `suspended` value ('hidden' in mOffice removes it from the bar).
  * Without this a Solid workflow module the actor cannot access would still show in the menu
  * (the bar defaults unreported modules to visible) and only reveal the denial on open.
  */
  toggleModuleVisibility() {
    this.publish('#ALL', 'mmStatus', {
      status: this.wf().model ? 'ok' : this.config.suspended
    });
  }

  /** Route the watch pushes into the client's reconciler. */
  mmObjects(sender, params) {
    this.wfclient.mmObjects(sender, params);
  }
  mmExit(sender, params) {
    this.wfclient.stop();
    super.mmExit(sender, params);
  }

  // ---- translations / model helpers ----

  /** Phrase prefixed with the workflow id (falls back through `wf.generic.` in LANG). */
  tr(key) {
    var _this$wfclient$slice$;
    return this.t(`wf.${((_this$wfclient$slice$ = this.wfclient.slice().model) === null || _this$wfclient$slice$ === void 0 ? void 0 : _this$wfclient$slice$.id) || 'generic'}.${key}`);
  }

  /** The model's color for an action on this record ('green'/'red'/'blue'/... or ''). */
  actionColor(record, action) {
    var _this$wfclient$engine, _this$wfclient$slice$2, _record$state;
    const states = ((_this$wfclient$engine = this.wfclient.engine) === null || _this$wfclient$engine === void 0 ? void 0 : _this$wfclient$engine.states) || ((_this$wfclient$slice$2 = this.wfclient.slice().model) === null || _this$wfclient$slice$2 === void 0 ? void 0 : _this$wfclient$slice$2.states) || {};
    return _.get(states, [(_record$state = record === null || record === void 0 ? void 0 : record.state) !== null && _record$state !== void 0 ? _record$state : '', 'actions', action, 'color'], '');
  }
  generateNewId() {
    var _this$wfclient$slice$3;
    return ((_this$wfclient$slice$3 = this.wfclient.slice().model) === null || _this$wfclient$slice$3 === void 0 ? void 0 : _this$wfclient$slice$3.id_generator) === 'client-oid' ? this.oid() : '';
  }

  // ---- record opening ----

  /**
  * @description
  * Selects a record for the read drawer and pulls its full field set + history logs (the
  * watch list is slim — file fields and logs are served only on a targeted get). The drawer
  * reads reactively, so it fills in as the full record reconciles.
  */
  openRecord(id) {
    this.setStore('selected', id);
    if (!id) return;
    this.wfclient.fetchFull(id, {
      logs: true
    }).then(record => {
      if (record) this.recordOpened(record);
    }).catch(e => {
      var _e$params;
      return this.warn('full record fetch failed: ' + _.toString((e === null || e === void 0 ? void 0 : (_e$params = e.params) === null || _e$params === void 0 ? void 0 : _e$params.error) || (e === null || e === void 0 ? void 0 : e.message) || e));
    });
  }

  /** Hook: the opened record arrived fully loaded. Subclasses enrich (lookups etc.). */
  recordOpened(record) {}

  // ---- actions ----

  /**
  * @description
  * Dispatches a workflow action from the GUI. Order of resolution:
  *   1. `customAction()` override (module-specific handling, e.g. local exports)
  *   2. the action's model form through `openForm()` (module-specific editors/wizards)
  *   3. destructive actions (DELETE/CLEAN/WIPE) behind a confirm modal
  *   4. plain `mmAction` — errors surface in an alert modal, history refreshes after
  */
  async runAction(record, action) {
    if (await this.customAction(record, action)) return;
    const form = this.wfclient.formFor(record, action);
    if (form && (await this.openForm(form, record, action))) return;
    if (/^(DELETE|CLEAN|WIPE)$/.test(action)) {
      const confirm_text = action === 'WIPE' ? this.tr('modal.ConfirmWipe') : this.tr('modal.ConfirmAction');
      const confirmed = await this.confirmModal(this.tr(`action.${action}`), confirm_text, {
        danger: true
      });
      if (!confirmed) return;
    }
    try {
      await this.wfclient.action(record.id, action, {});
      // the action wrote a new log row — refresh the open drawer's history
      if (action === 'WIPE' && this.store.selected === record.id) this.setStore('selected', '');else if (this.store.selected === record.id) this.wfclient.fetchFull(record.id, {
        logs: true
      }).catch(() => {});
    } catch (e) {
      var _e$params2;
      const error = _.toString(e !== null && e !== void 0 && (_e$params2 = e.params) !== null && _e$params2 !== void 0 && _e$params2.error ? this.t(e.params.error) : (e === null || e === void 0 ? void 0 : e.message) || e);
      this.warn('action ' + action + ' failed: ' + error);
      this.alertModal(this.tr(`action.${action}`), error);
    }
  }

  /** Hook: handle an action entirely in the module (return true when handled). */
  async customAction(record, action) {
    return false;
  }

  /** Hook: open the module's editor for the action's model form (return true when handled). */
  async openForm(form, record, action) {
    return false;
  }
} // mmodSolidWorkflow